import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDateTime } from '@/lib/format'

export function AuditLogs() {
  const { t, i18n } = useTranslation('admin')
  const [action, setAction] = useState('')
  const [resourceType, setResourceType] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['admin-audit', action, resourceType, page],
    queryFn: () =>
      adminApi.listAuditLogs({
        ...(action ? { action } : {}),
        ...(resourceType ? { resourceType } : {}),
        page,
      }),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('audit.title')}</h1>

      <div className="flex flex-wrap items-end gap-3">
        <input
          className="clari-input max-w-xs"
          placeholder={t('audit.filter_action')}
          value={action}
          onChange={(e) => {
            setAction(e.target.value)
            setPage(1)
          }}
          aria-label={t('audit.filter_action')}
        />
        <input
          className="clari-input max-w-xs"
          placeholder={t('audit.filter_resource')}
          value={resourceType}
          onChange={(e) => {
            setResourceType(e.target.value)
            setPage(1)
          }}
          aria-label={t('audit.filter_resource')}
        />
      </div>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('audit.col_action')}</th>
                <th className="px-4 py-2 font-medium">{t('audit.col_actor')}</th>
                <th className="px-4 py-2 font-medium">{t('audit.col_resource')}</th>
                <th className="px-4 py-2 font-medium">{t('audit.col_time')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((log) => (
                <tr key={log.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-mono">{log.action}</td>
                  <td className="px-4 py-2 text-text-muted">{log.actor_type}</td>
                  <td className="px-4 py-2 text-text-muted">{log.resource_type ?? '—'}</td>
                  <td className="px-4 py-2 text-text-muted">
                    {formatDateTime(log.timestamp, i18n.language)}
                  </td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-text-muted">
                    {t('audit.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
